<template>
    <Row class="CollateralTabRow">
        <Cell
            v-for="({ name, field }) in columns"
            :key="name"
            :data-test-id="`CollateralTabRow__${name}Cell`"
        >
            <div v-if="name==='action'" class="flex justify-between items-center">
                <DocumentDataOnlyBadge v-if="isShowingDataOnlyBadge" />
                <DocumentViewButton v-else-if="isShowingDocumentViewButton" @click="showDocument" />
                <div v-else-if="isNonCompliantWithoutCoverage" class="text-eidQualityBlack-50 text-sm flex">
                    <EidIcon
                        :icon-src="iconError"
                        class="text-eidDangerRed"
                        alt="error"
                    />
                    <span class="inline-block text-eidDangerRed ml-2">Not Provided</span>
                </div>
                <EvaluationError
                    v-if="showGrandRemoveException "
                    :show-go-to-criterion="false"
                    :show-error-message="false"
                    :show-exception-badge="false"
                    :evaluation-error="evaluationError"
                    data-test-id="CollateralTabRow__evaluationExceptionGrandRemove"
                    @grant-exception-to-criterion="grantEvaluateEntityException"
                    @remove-exceptions="removeExceptions"
                >
                    <template #grant-exception-label>
                        Grant exceptions for this collateral.
                    </template>
                    <template #grant-exception-sub-label>
                        All current and future non-compliant criteria
                        <span class="text-eidTrustGreen">will be waived as compliant. This can be undone.</span>
                    </template>
                </EvaluationError>
            </div>
            <div v-else-if="name==='description-uniqIdentifier'" class="flex flex-col">
                <span
                    data-test-id="CollateralTabRow__collateralDescription"
                    :class="{'text-eidDarkBlue': !isNonCompliant, 'text-eidDangerRed': isNonCompliant }"
                >
                    {{ collateral['description'] }}
                    <span v-if="isCompliantStatusPending" class="text-eidDarkBlue-50"> - Pending</span>
                </span>
                <span data-test-id="CollateralTabRow__collateralUniqueIdentifier" class="text-eidDarkBlue-50">
                    {{ collateral['uniqueIdentifier'] }}
                </span>
            </div>
            <template v-else-if="isNonCollateralField(field)">
                <div
                    v-if="isNonCompliantWithoutCoverage || fieldHasEvaluationError(field)"
                    v-tooltip="getFieldTooltipMessage(field)"
                    class="inline-flex"
                >
                    <EidIcon
                        :icon-src="iconError"
                        :class="{ 'text-eidDangerRed': !hasException, 'text-eidImpactOrange': hasException }"
                        alt="error"
                        :data-test-id="hasException ? 'CollateralTabRow__exceptionIcon' : 'CollateralTabRow__errorIcon'"
                    />
                    <span
                        class="inline-block ml-3"
                        :class="{ 'text-eidDangerRed' : !hasException }"
                    >
                        {{ getValue(field) }}
                    </span>
                </div>
                <template v-else>
                    {{ getValue(field) }}
                </template>
            </template>
            <template v-else>
                {{ getValue(field) }}
            </template>
        </Cell>
    </Row>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { intersection } from 'lodash';

    import { faFileAlt } from '@fortawesome/free-regular-svg-icons';
    import { faExclamationCircle, faExclamationTriangle, IconDefinition } from '@fortawesome/free-solid-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { Cell, Row } from '@evidentid/dashboard-commons/components/Table';
    import { SubmissionMethod } from '@evidentid/tprm-portal-lib/models/common/SubmissionMethod.model';
    import {
        ComplianceStatus,
        ComplianceStatusMetadata,
        EvaluationResult,
    } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { CollateralEntity } from '@evidentid/tprm-portal-lib/models/entity-details/Collateral/CollateralEntity.model';
    import { EntityRequirementDetails } from '@evidentid/tprm-portal-lib/models/entity-details/EntityRequirement.model';

    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { EnumCategories } from '@/modules/dashboard/models/EnumCategories.model';
    import DocumentDataOnlyBadge from '@/modules/entity-details/components/DocumentDataOnlyBadge/DocumentDataOnlyBadge.vue';
    import DocumentViewButton from '@/modules/entity-details/components/DocumentViewButton/DocumentViewButton.vue';
    import EvaluationError from '@/modules/entity-details/components/EvaluationError/EvaluationError.vue';
    import { CollateralEntityDisplayField } from '@/modules/entity-details/models/CollateralEntityDisplayField.model';
    import { CollateralErrorType } from '@/modules/entity-details/models/CollateralErrorType.model';
    import { TableColumn } from '@/modules/entity-details/models/TableColumn.model';
    import { createEvaluateEntityLevelException } from '@/modules/entity-details/utils/createException';
    import { currencyFormatter } from '@/modules/entity-details/utils/currencyFormatter';
    import {
        getCollateralShortError,
        getPossibleErrorTypes,
        getTypesFromErrors,
        isFieldValueMissing,
    } from '@/modules/entity-details/utils/getCollateralErrorAndType/getCollateralErrorAndType';
    import {
        isFieldImportApiOrWithoutDocumentUpload,
    } from '@/modules/entity-details/utils/isFieldImportApiOrWithoutDocumentUpload/isFieldImportApiOrWithoutDocumentUpload';
    import { getEnumDefaultDisplayValue } from '@/utils/getEnumDefaultDisplayValue';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';

    function getFoundTypes(errors: string[], isCollateralNotProvided: boolean): CollateralErrorType[] {
        const foundTypes = getTypesFromErrors(errors);
        if (isCollateralNotProvided) {
            foundTypes.push(CollateralErrorType.collateralNotProvided);
        }
        return foundTypes;
    }

    export default defineComponent({
        name: 'CollateralTabRow',
        components: {
            DocumentDataOnlyBadge,
            DocumentViewButton,
            Row,
            Cell,
            EidIcon,
            EvaluationError,
        },
        props: {
            collateral: {
                type: Object as PropType<CollateralEntity>,
                required: true,
            },
            columns: {
                type: Array as PropType<TableColumn[]>,
                required: true,
            },
            evaluationResults: {
                type: Array as PropType<EvaluationResult[]>,
                required: true,
            },
            entityContactEmail: {
                type: String as PropType<string>,
                default: '',
            },
            entityId: {
                type: String as PropType<string>,
                required: true,
            },
            requirementDetails: {
                type: Object as PropType<EntityRequirementDetails | null>,
                default: null,
                required: false,
            },
        },
        emits: [ 'grant-evaluate-entity-exception', 'remove-exceptions' ],
        data() {
            return {
                iconViewDocument: faFileAlt,
                SubmissionMethod,
            };
        },
        computed: {
            submissionMethod(): SubmissionMethod | null {
                return this.requirementDetails?.coverage?.submissionMethod ?? null;
            },
            iconError(): IconDefinition {
                return this.hasException ? faExclamationCircle : faExclamationTriangle;
            },
            isNonCompliant(): boolean {
                return this.collateral.complianceStatus === ComplianceStatus.nonCompliant;
            },
            isCompliantStatusPending(): boolean {
                return this.collateral.complianceStatus === ComplianceStatus.pending;
            },
            hasValidCollateralForDocumentButtonOrBadge(): boolean {
                return !this.isNonCompliantWithoutCoverage &&
                    ![ ComplianceStatus.pending, ComplianceStatus.new ]
                        .includes(this.collateral.complianceStatus);
            },
            isFieldImportApiOrWithoutDocumentUploadVal(): boolean {
                if (!this.submissionMethod) {
                    return false;
                }
                return isFieldImportApiOrWithoutDocumentUpload({
                    submissionMethod: this.submissionMethod,
                });
            },
            isShowingDataOnlyBadge(): boolean {
                return this.hasValidCollateralForDocumentButtonOrBadge
                    && this.isFieldImportApiOrWithoutDocumentUploadVal;
            },
            isShowingDocumentViewButton(): boolean {
                return this.hasValidCollateralForDocumentButtonOrBadge
                    && !this.isFieldImportApiOrWithoutDocumentUploadVal;
            },
            isNonCompliantWithoutCoverage(): boolean {
                return this.isNonCompliant && !this.collateral.coverageType;
            },
            isCollateralNotProvided(): boolean {
                return this.isNonCompliantWithoutCoverage &&
                    this.collateral.coverageLimit == null &&
                    this.collateral.coverageDeductible == null &&
                    this.collateral.coverageLimitType == null &&
                    this.collateral.coverageValuation == null;
            },
            categorizedEnumLabels(): CategorizedEnumLabels {
                // TODO(PRODUCT-18675): remove any after the migration to the newer version.
                return useDashboardStore().categorizedEnumLabels;
            },
            errorTypes(): CollateralErrorType[] {
                const errors = [] as string[];
                this.evaluationResults.forEach((result) => {
                    if (result.result === ComplianceStatus.nonCompliant && result.nonComplianceMessage) {
                        errors.push(result.nonComplianceMessage);
                    }
                });
                return getFoundTypes(errors, this.isCollateralNotProvided);
            },
            errorTypesWithException(): CollateralErrorType[] {
                const errors = [] as string[];
                this.evaluationResults.forEach((result) => {
                    if (
                        result.result === ComplianceStatus.compliant &&
                        result.failures.length > 0 &&
                        result.nonComplianceMessage
                    ) {
                        errors.push(result.nonComplianceMessage);
                    }
                });
                return getFoundTypes(errors, this.isCollateralNotProvided);
            },
            showGrandRemoveException(): boolean {
                return Boolean(this.evaluationError) && (this.hasException || this.errorTypes.length > 0);
            },
            hasException(): boolean {
                return this.collateral.complianceStatus === ComplianceStatus.compliant &&
                    this.errorTypesWithException.length > 0;
            },
            evaluationError(): {
                evaluatedEntityId?: string;
                complianceStatusMetadata: ComplianceStatusMetadata[];
            } | null {
                if (this.evaluationResults.length === 0) {
                    return null;
                }

                return {
                    evaluatedEntityId: this.collateral.evaluatedEntityId,
                    complianceStatusMetadata: this.evaluationResults[0].complianceStatusMetadata ?
                        [ this.evaluationResults[0].complianceStatusMetadata ]
                        : [],
                };
            },
        },
        methods: {
            showDocument(): void {
                const verificationId = this.requirementDetails?.coverage?.requestId;
                if (!verificationId) {
                    return;
                }
                // TODO(PRODUCT-18675): fix any after the migration to the newer version.
                (this as any).$procedures.execute('showEntityRequirementRprDocuments', {
                    rpName: (this as any).$rp.current!,
                    entityId: this.entityId,
                    email: this.entityContactEmail || '',
                    requirementType: this.collateral.coverageType,
                    verificationId,
                });
            },
            isNonCollateralField(field: string): boolean {
                return [ 'coverageLimit', 'coverageDeductible', 'coverageType' ].includes(field);
            },
            getValue(field: string): unknown {
                if (
                    this.isNonCollateralField(field)
                    && this.isCompliantStatusPending
                ) {
                    return 'Pending';
                }

                const value = this.collateral[field as keyof CollateralEntity];

                if (field === 'coverageLimit') {
                    const valuation = this.collateral.coverageValuation || '';
                    const limitValue = value == null ? '' : currencyFormatter().format(Number(value));
                    const separator = value != null && valuation ? ' - ' : '';
                    const finalValue = `${limitValue}${separator}${valuation}`;
                    return finalValue || '-';
                }

                if (value === null || value === undefined) {
                    return '-';
                }

                if (field === 'coverageType') {
                    return getEnumDefaultDisplayValue(
                        EnumCategories.coverageTypes,
                        value,
                        this.categorizedEnumLabels,
                    );
                }

                if ([ 'maximumDeductible', 'limitRequired', 'coverageDeductible', 'coverageLimit' ].includes(field)) {
                    return currencyFormatter().format(Number(value));
                }

                return value;
            },
            isNotProvidedField(field: CollateralEntityDisplayField): boolean {
                const value = this.collateral[field as keyof CollateralEntity];
                let includesNotProvidedError;

                if (this.hasException) {
                    includesNotProvidedError = this.errorTypesWithException
                        .includes(CollateralErrorType.collateralNotProvided);
                } else {
                    includesNotProvidedError = this.errorTypes.includes(CollateralErrorType.collateralNotProvided);
                }

                return includesNotProvidedError && value == null;
            },
            isFieldValueMissing(field: CollateralEntityDisplayField): boolean {
                // Non-compliance is only known at the row level, not per field, so this fallback
                // can't tell whether limit/valuation/limitType were actually required - scoping it
                // to deductible avoids flagging fields that were legitimately never required.
                if (field !== CollateralEntityDisplayField.coverageDeductible) {
                    return false;
                }
                const value = this.collateral[field as keyof CollateralEntity];
                const isFieldNonCompliant = this.hasException
                    ? this.errorTypesWithException.length > 0
                    : this.isNonCompliant;
                return isFieldValueMissing(value, isFieldNonCompliant);
            },
            fieldHasEvaluationError(field: CollateralEntityDisplayField): boolean {
                const errors = this.getFieldTooltipMessage(field);
                return Boolean(errors && errors.length > 0);
            },
            getFieldTooltipMessage(field: CollateralEntityDisplayField): string | null {
                if (this.isNotProvidedField(field)) {
                    return getCollateralShortError(CollateralErrorType.collateralNotProvided);
                }

                const possibleErrorTypesOfField = this.getPossibleErrorTypesPerField(field);
                let errorTypes = [];

                if (this.hasException) {
                    errorTypes = intersection(this.errorTypesWithException, possibleErrorTypesOfField);
                } else {
                    errorTypes = intersection(this.errorTypes, possibleErrorTypesOfField);
                }

                if (errorTypes.length > 0) {
                    return errorTypes.map(getCollateralShortError).join('\n');
                }

                return this.isFieldValueMissing(field)
                    ? getCollateralShortError(CollateralErrorType.fieldValueMissing)
                    : null;
            },
            getPossibleErrorTypesPerField(field: CollateralEntityDisplayField): CollateralErrorType[] {
                return field === CollateralEntityDisplayField.coverageLimit
                    ? this.getCoverageLimitPossibleErrorTypes()
                    : getPossibleErrorTypes(field);
            },
            getCoverageLimitPossibleErrorTypes(): CollateralErrorType[] {
                const possibleErrorTypes = [
                    ...getPossibleErrorTypes(CollateralEntityDisplayField.coverageLimit),
                    ...getPossibleErrorTypes(CollateralEntityDisplayField.coverageLimitType),
                    ...getPossibleErrorTypes(CollateralEntityDisplayField.coverageValuation),
                ];
                return [ ...new Set(possibleErrorTypes) ];
            },
            grantEvaluateEntityException(): void {
                const exception = createEvaluateEntityLevelException(this.collateral.evaluatedEntityId);
                this.$emit('grant-evaluate-entity-exception', [ exception ]);
            },
            removeExceptions(exceptionIds: string[]): void {
                this.$emit('remove-exceptions', exceptionIds);
            },
        },
    });
</script>
